import { useCallback, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import ModalBasicLayout from "../ModalBasicLayout";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_BULK_CREATE_CAMPAIGNS,
  BUTTON_CREATE_FROM_SCRATCH,
  BUTTON_DUPLICATE_FROM_PLATFORM,
  BUTTON_IMPORT_FROM_PLATFORM,
  CREATE_CAMPAIGN_WIZARD_ARIA,
} from "../../../i18n/keysTranslations";
import SegmentedControl from "../../SegmentedControl/SegmentedControl";
import ModalImportAdsCampaign from "../ModalImportAdsCampaign/ModalImportAdsCampaign";
import ModalBulkCampaigns from "../ModalBulkCampaigns/ModalBulkCampaigns";
import useAgencyHasImportCampaignsFeature from "../../../hooks/useAgencyHasImportCampaignsFeature";
import CreateCampaignPlatformFlow from "./CreateCampaignPlatformFlow";
import CreateCampaignSetup from "./steps/CreateCampaignSetup";
import {
  CREATE_CAMPAIGN_MODAL_MAX_WIDTH,
  CREATE_CAMPAIGN_PHASE,
} from "./createAdsCampaignWizardConstants";
import { hasUnsavedCreateCampaignProgress } from "./createAdsCampaignWizardUtils";
import { startGenerateInitialCampaign } from "./startGenerateInitialCampaign";
import { useCreateAdsCampaignWizard } from "./useCreateAdsCampaignWizard";

// Pestaña "Duplicar" oculta por ahora; cambiar a true para habilitarla
const SHOW_DUPLICATE_MODE = true;

const CREATE_MODE = {
  CREATE: "create",
  IMPORT: "import",
  DUPLICATE: "duplicate",
  BULK: "bulk",
};

const ModalCreateAdsCampaign = ({
  modalOpen,
  onCloseModal,
  brandInformation = {},
  initialCreatives = null,
  resumeSession = null,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const wizard = useCreateAdsCampaignWizard({
    modalOpen,
    brandInformation,
    initialCreatives,
    resumeSession,
  });
  const hasProgress = hasUnsavedCreateCampaignProgress({
    formState: wizard.formState,
    phase: wizard.phase,
    selectedPlatform: wizard.selectedPlatform,
  });
  const hasImportCampaignsFeature = useAgencyHasImportCampaignsFeature();
  const [mode, setMode] = useState(CREATE_MODE.CREATE);
  const modeOptions = useMemo(
    () => [
      { value: CREATE_MODE.CREATE, label: t(BUTTON_CREATE_FROM_SCRATCH) },
      ...(hasImportCampaignsFeature
        ? [{ value: CREATE_MODE.IMPORT, label: t(BUTTON_IMPORT_FROM_PLATFORM) }]
        : []),
      ...(SHOW_DUPLICATE_MODE
        ? [
            {
              value: CREATE_MODE.DUPLICATE,
              label: t(BUTTON_DUPLICATE_FROM_PLATFORM),
            },
          ]
        : []),
      { value: CREATE_MODE.BULK, label: t(BUTTON_BULK_CREATE_CAMPAIGNS) },
    ],
    [hasImportCampaignsFeature, t]
  );
  const isBusy = wizard.isSelectingPlatform;
  const isSetupPhase = wizard.phase === CREATE_CAMPAIGN_PHASE.SETUP;

  const onCloseWizard = useCallback(() => {
    onCloseModal();
  }, [onCloseModal]);

  const onSavePlatform = useCallback(
    (platformData) => {
      const campaignID = wizard.campaignID;
      if (!campaignID || !platformData?.platform) {
        return;
      }

      const setupFields = {
        context: wizard.formState.context,
        language: wizard.formState.language,
        locations: wizard.formState.locations,
        attachmentsFiles: wizard.formState.attachmentsFiles,
        fileNamesMapping: wizard.formState.fileNamesMapping,
      };

      dispatch(
        startGenerateInitialCampaign({
          campaignID,
          setupFields,
          platformData,
        })
      );
      onCloseModal();
    },
    [dispatch, onCloseModal, wizard]
  );

  const renderPhase = () => {
    switch (wizard.phase) {
      case CREATE_CAMPAIGN_PHASE.SETUP:
        return (
          <CreateCampaignSetup
            disabled={isBusy}
            error={wizard.setupError}
            businessName={brandInformation?.businessName || ""}
            onSelectPlatform={wizard.onSelectPlatform}
          />
        );
      case CREATE_CAMPAIGN_PHASE.PLATFORM:
        return (
          <CreateCampaignPlatformFlow
            platform={wizard.selectedPlatform}
            campaignID={wizard.campaignID}
            initialData={wizard.platformInitialData}
            galleryHydrationRef={wizard.galleryHydrationRef}
            context={{
              locations: wizard.formState.locations,
              language: wizard.formState.language,
              campaignContext: wizard.formState.context,
            }}
            onCallbackSave={onSavePlatform}
            onExitToPlatformSelection={wizard.onExitToPlatformSelection}
            resumeValues={wizard.resumeValues}
          />
        );
      case CREATE_CAMPAIGN_PHASE.GENERATING:
      case CREATE_CAMPAIGN_PHASE.ERROR:
        return null;
      default: {
        const _exhaustive = wizard.phase;
        void _exhaustive;
        return null;
      }
    }
  };

  const showModeSelector =
    modeOptions.length > 1 && (mode !== CREATE_MODE.CREATE || isSetupPhase);

  const renderMode = () => {
    switch (mode) {
      case CREATE_MODE.CREATE:
        return renderPhase();
      case CREATE_MODE.IMPORT:
        return (
          <ModalImportAdsCampaign
            embedded
            modalOpen={modalOpen}
            onCloseModal={onCloseWizard}
          />
        );
      case CREATE_MODE.DUPLICATE:
        return (
          <ModalImportAdsCampaign
            embedded
            duplicateStructureOnly
            modalOpen={modalOpen}
            onCloseModal={onCloseWizard}
          />
        );
      case CREATE_MODE.BULK:
        return (
          <ModalBulkCampaigns
            embedded
            modalOpen={modalOpen}
            onCloseModal={onCloseWizard}
          />
        );
      default: {
        const _exhaustive = mode;
        void _exhaustive;
        return null;
      }
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseWizard}
      confirmClose={hasProgress && mode === CREATE_MODE.CREATE}
      fullWidth={true}
      maxWidth="md"
      scroll="paper"
      ariaLabel={t(CREATE_CAMPAIGN_WIZARD_ARIA)}
      title=""
      titleContent={false}
      variantTitle="h5"
      sxTitle={{
        justifyContent: "flex-end",
      }}
      sxPaper={{
        maxWidth: CREATE_CAMPAIGN_MODAL_MAX_WIDTH,
      }}
      sxContent={
        isSetupPhase && mode === CREATE_MODE.CREATE
          ? {
              px: { xs: 2, sm: 5, md: 10 },
              pb: { xs: 2, sm: 2, md: 5 },
            }
          : undefined
      }
      topContent={
        showModeSelector ? (
          <SegmentedControl
            name="create_campaign_mode"
            options={modeOptions}
            selectedValue={mode}
            onChange={setMode}
          />
        ) : null
      }
    >
      <FormProvider {...wizard.methods}>{renderMode()}</FormProvider>
    </ModalBasicLayout>
  );
};

export default ModalCreateAdsCampaign;
